import React, { useEffect, useState } from "react";
import api, { apiErrorMsg } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { ListChecks, Plus, Check, X, Search } from "lucide-react";
import { toast } from "sonner";

/**
 * v2.71.13 — Bundle picker popup.
 * Mounted by <AddToShortlistButton> on PropertyCard. Lets admin/team:
 *   • Search/scroll their existing bundles
 *   • Click an existing bundle → adds the property (idempotent on backend)
 *   • OR create a new bundle inline → adds property in one shot
 *
 * Endpoint: POST /api/admin/shortlists (and /properties sub-route).
 * `require_admin` accepts both admin & team roles, ownership filtering applies.
 */
export default function ShortlistBundlePicker({ open, onClose, propertyId, propertyTitle }) {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newClient, setNewClient] = useState("");
  const [newNotes, setNewNotes] = useState("");

  const loadBundles = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/shortlists", { params: query ? { q: query } : {} });
      setBundles(r.data || []);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Failed to load bundles"));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      loadBundles();
      setQuery("");
      setCreating(false);
      setNewName("");
      setNewClient("");
      setNewNotes("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(loadBundles, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const addToBundle = async (sid) => {
    setBusyId(sid);
    try {
      const r = await api.post(`/admin/shortlists/${sid}/properties`, { property_id: propertyId });
      if (r.data?.added) toast.success("Added to shortlist");
      else toast.info("Already in this shortlist");
      onClose?.();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Failed to add"));
    }
    setBusyId(null);
  };

  const createAndAdd = async () => {
    if (!newName.trim()) {
      toast.error("Bundle name required");
      return;
    }
    setBusyId("__new__");
    try {
      const r = await api.post("/admin/shortlists", {
        name: newName.trim(),
        client_name: newClient.trim(),
        notes: newNotes.trim(),
        property_ids: [propertyId],
      });
      toast.success(`Created "${r.data.name}" and added property`);
      onClose?.();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Create failed"));
    }
    setBusyId(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto rounded-sm" data-testid="shortlist-bundle-picker">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-xl flex items-center gap-2">
            <ListChecks size={18} className="text-[#16A34A]" />
            Add to Shortlist
          </DialogTitle>
          {propertyTitle && (
            <div className="text-[12px] text-zinc-500 truncate">{propertyTitle}</div>
          )}
        </DialogHeader>

        {!creating ? (
          <>
            <div className="relative my-3">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search bundles by name, client, or note…"
                className="pl-8 h-9 text-[13px]"
                data-testid="bundle-picker-search"
              />
            </div>

            <div className="space-y-1.5 min-h-[120px]" data-testid="bundle-picker-list">
              {loading && <div className="text-[12px] text-zinc-400 py-6 text-center">Loading…</div>}
              {!loading && bundles.length === 0 && (
                <div className="text-[13px] text-zinc-500 py-6 text-center border border-dashed border-[#E4E4E7] rounded-sm">
                  No bundles yet — create your first one below.
                </div>
              )}
              {!loading && bundles.map((b) => (
                <button
                  key={b.id}
                  onClick={() => addToBundle(b.id)}
                  disabled={busyId === b.id}
                  className="w-full text-left p-3 border border-[#E4E4E7] rounded-sm hover:border-[#16A34A] hover:bg-[#F0FDF4] transition-colors flex items-center justify-between gap-3 disabled:opacity-50"
                  data-testid={`bundle-row-${b.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-zinc-900 truncate">{b.name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">
                      {b.client_name ? `${b.client_name} • ` : ""}
                      {b.property_count || 0} properties
                      {b.view_count ? ` • ${b.view_count} views` : ""}
                    </div>
                  </div>
                  {busyId === b.id ? (
                    <span className="text-[11px] text-zinc-400">Adding…</span>
                  ) : (
                    <Plus size={16} className="text-[#16A34A] flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>

            <Button
              onClick={() => setCreating(true)}
              variant="outline"
              className="w-full mt-3 rounded-sm border-dashed border-[#16A34A] text-[#15803D] hover:bg-[#F0FDF4]"
              data-testid="bundle-picker-create-new"
            >
              <Plus size={14} className="mr-1" />
              Create new shortlist
            </Button>
          </>
        ) : (
          <div className="space-y-3 mt-2" data-testid="bundle-picker-create-form">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                Shortlist name *
              </label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder='e.g. "Sharma Family — 3BHK options"'
                className="mt-1 h-9 text-[13px]"
                data-testid="new-bundle-name"
                autoFocus
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                Client name (optional)
              </label>
              <Input
                value={newClient}
                onChange={(e) => setNewClient(e.target.value)}
                placeholder="Mr Sharma"
                className="mt-1 h-9 text-[13px]"
                data-testid="new-bundle-client"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                Notes (optional)
              </label>
              <Textarea
                value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)}
                placeholder="Looking for north-facing, ready-to-move-in…"
                className="mt-1 text-[13px] min-h-[60px]"
                data-testid="new-bundle-notes"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                variant="ghost"
                onClick={() => setCreating(false)}
                className="flex-1 rounded-sm h-9 text-[13px]"
              >
                <X size={14} className="mr-1" /> Back
              </Button>
              <Button
                onClick={createAndAdd}
                disabled={busyId === "__new__"}
                className="flex-1 rounded-sm h-9 bg-[#16A34A] hover:bg-[#15803D] text-white text-[13px]"
                data-testid="new-bundle-submit"
              >
                <Check size={14} className="mr-1" />
                {busyId === "__new__" ? "Creating…" : "Create & Add"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
